import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useNavigate} from "react-router-dom";

import styles from './SupportTicketsListPage.module.scss';
import techSupportIcon from '../../constants/images/detailsOrder/tech-support.svg';
import {supportActions} from "../../store/slices/supportSlice";
import {MAIN_ROUTES} from "../../routing/main_routes";
import Loader from "../../components/Loader/Loader";
import SupportCategoryModal from "../../components/SupportCategoryModal/SupportCategoryModal";

const STATUS_LABELS = {
    open: 'Відкрито',
    in_progress: 'В роботі',
    resolved: 'Вирішено',
    closed: 'Закрито',
};

const STATUS_MOD = {
    open: 'open',
    in_progress: 'in_progress',
    resolved: 'resolved',
    closed: 'closed',
};

const SupportTicketsListPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const {tickets, status} = useSelector((state) => state.support);

    // auto-open create modal when navigated with state.autoOpen
    const [isModalOpen, setIsModalOpen] = useState(
        !!(location.state && location.state.autoOpen)
    );
    const [modalOrderId, setModalOrderId] = useState(
        location.state ? location.state.orderId : null
    );

    useEffect(() => {
        const fetchTickets = async () => {
            try {
                await dispatch(supportActions.listTickets());
            } catch (error) {
                console.error(error);
            }
        };

        fetchTickets();

        // poll every 30s
        const pollInterval = setInterval(() => {
            dispatch(supportActions.listTickets());
        }, 30000);

        return () => clearInterval(pollInterval);
    }, [dispatch]);

    const handleOpenCreate = () => {
        setModalOrderId(null);
        setIsModalOpen(true);
    };

    const handleTicketClick = (ticketId) => {
        navigate(`${MAIN_ROUTES.SUPPORT_CHAT}/${ticketId}`);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('uk-UA', {hour: '2-digit', minute: '2-digit'});
        }
        if (diffDays === 1) {
            return 'Вчора';
        }
        return date.toLocaleDateString('uk-UA', {day: '2-digit', month: '2-digit'});
    };

    if (status === 'loading' && tickets.length === 0) {
        return <Loader/>;
    }

    return (
        <div className={styles.supportListPage_wrapper}>
            <div className={styles.supportListPage}>
                <div className={styles.supportListPage_header}>
                    <h1 className={styles.supportListPage_header_title}>Мої звернення</h1>
                    <button
                        className={styles.supportListPage_header_newBtn}
                        onClick={handleOpenCreate}
                    >
                        + Нове звернення
                    </button>
                </div>

                {tickets.length === 0 ? (
                    <div className={styles.supportListPage_empty}>
                        <img
                            className={styles.supportListPage_empty_icon}
                            src={techSupportIcon}
                            alt="tech support"
                        />
                        <p className={styles.supportListPage_empty_title}>У вас немає звернень</p>
                        <p className={styles.supportListPage_empty_subtitle}>
                            Якщо у вас виникли питання або проблеми із замовленням, створіть нове звернення.
                        </p>
                    </div>
                ) : (
                    <div className={styles.supportListPage_list}>
                        {tickets.map((ticket) => (
                            <div
                                key={ticket.id}
                                className={styles.ticketCard}
                                onClick={() => handleTicketClick(ticket.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === 'Enter' && handleTicketClick(ticket.id)}
                            >
                                <div className={styles.ticketCard_icon}>
                                    <img src={techSupportIcon} alt="ticket icon"/>
                                </div>

                                <div className={styles.ticketCard_content}>
                                    <div className={styles.ticketCard_content_top}>
                                        <p className={styles.ticketCard_content_subject}>
                                            {ticket.subject || ticket.category_display || 'Звернення'}
                                        </p>
                                        <span className={styles.ticketCard_date}>
                                            {formatDate(ticket.last_message_at || ticket.updated_on)}
                                        </span>
                                    </div>

                                    {ticket.last_message && (
                                        <p className={styles.ticketCard_content_preview}>
                                            {ticket.last_message}
                                        </p>
                                    )}

                                    <div className={styles.ticketCard_content_meta}>
                                        <span
                                            className={`${styles.ticketCard_badge_status} ${styles[`ticketCard_badge_status--${STATUS_MOD[ticket.status] || 'open'}`]}`}
                                        >
                                            {ticket.status_display || STATUS_LABELS[ticket.status] || ticket.status}
                                        </span>

                                        {ticket.unread_count > 0 && (
                                            <span className={styles.ticketCard_badge_unread}>
                                                {ticket.unread_count}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <SupportCategoryModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                orderId={modalOrderId}
            />
        </div>
    );
};

export default SupportTicketsListPage;
